import toast from "react-hot-toast";
import { toastAiCredits } from "./aiCredits";

interface ToastMessages {
  loading: string;
  success: string;
  error: string | ((error: unknown) => string);
}

/**
 * Wraps an async operation with toast notifications
 * @param operation - The async function to execute
 * @param messages - Toast messages for loading, success, and error states
 * @param toastId - Unique ID for the toast (to replace previous toasts)
 * @returns The result of the operation
 */
export async function withToast<T>(
  operation: () => Promise<T>,
  messages: ToastMessages,
  toastId: string
): Promise<T> {
  toast.loading(messages.loading, { id: toastId });

  try {
    const result = await operation();
    toast.success(messages.success, { id: toastId });
    return result;
  } catch (error) {
    // A spent AI balance has its own toast — no caller can phrase it better than "ask for a top-up".
    if (toastAiCredits(error, toastId)) throw error;

    const errorMessage =
      typeof messages.error === "function" ? messages.error(error) : messages.error;
    toast.error(errorMessage, { id: toastId });
    throw error;
  }
}
